import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pack = location.state;

  const [phone, setPhone] = useState("");
  const [provider, setProvider] = useState("mtn");
  const [reference, setReference] = useState("");

  const [showOTP, setShowOTP] = useState(false);
  const [showPIN, setShowPIN] = useState(false);

  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");

  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [otpCountdown, setOtpCountdown] = useState(0);

  const PAYSTACK_PROXY_URL = "https://installmentzone.com/pharmacy/api/paystack_proxy.php";

  if (!pack) {
    navigate("/renew-license");
    return null;
  }

  const amount = pack.price * 100; // Correct: pesewas

  const formatGhc = (val) => `GHS ${val.toFixed(2)}`;

  // Poll verification (defined early so it can be called from anywhere)
  const pollPayment = (ref) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.post(PAYSTACK_PROXY_URL, {
          action: "verify",
          reference: ref,
        });

        if (res.data.success && res.data.data?.status === "success") {
          clearInterval(interval);
          setStatusMessage("Payment confirmed ✓ Activating license...");
          await activateLicense(ref);
        }
      } catch (err) {
        console.error(err);
      }
    }, 6000);

    setTimeout(() => clearInterval(interval), 15 * 60 * 1000);
  };

  // Start payment
  const startPayment = async () => {
    if (!phone || phone.length < 9) {
      setErrorMessage("Please enter a valid mobile money number");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setStatusMessage("Initializing payment... Please wait");

    const payload = {
      action: "charge",
      amount,
      email: "user@example.com",
      currency: "GHS",
      mobile_money: { phone, provider },
      metadata: { pack: pack.code },
    };

    try {
      const res = await axios.post(PAYSTACK_PROXY_URL, payload);

      if (!res.data.success) {
        throw new Error(res.data.message || "Payment initiation failed");
      }

      const { reference: ref, status, display_text, gateway_response } = res.data.data;

      setReference(ref);

      setStatusMessage(
        display_text ||
        gateway_response ||
        "Payment prompt sent! Check your phone now."
      );

      if (status === "send_otp" || (status === "pending" && gateway_response?.toLowerCase().includes("otp"))) {
        setShowOTP(true);
        setOtpCountdown(300);
      } else if (status === "send_pin" || (status === "pending" && gateway_response?.toLowerCase().includes("pin"))) {
        setShowPIN(true);
      } else if (["pending", "pay_offline", "success"].includes(status)) {
        setStatusMessage("Waiting for confirmation on your phone...");
        pollPayment(ref);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(
        err?.response?.data?.message ||
        err.message ||
        "Failed to start payment. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Submit OTP
  const submitOTP = async () => {
    if (!otp || otp.length < 4) {
      setErrorMessage("Please enter a valid OTP");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setStatusMessage("Verifying OTP... Do not close this page");

    try {
      const res = await axios.post(PAYSTACK_PROXY_URL, {
        action: "submit_otp",
        reference,
        otp,
      });

      if (!res.data.success) {
        throw new Error(res.data.message || "OTP verification failed");
      }

      const { status } = res.data.data;

      if (status === "success") {
        setStatusMessage("Payment completed! Activating your license...");
        await activateLicense(reference);
      } else if (status === "send_pin") {
        setShowOTP(false);
        setShowPIN(true);
        setStatusMessage("OTP verified ✓ Now enter your MoMo PIN to approve.");
      } else {
        pollPayment(reference);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(
        err?.response?.data?.message ||
        "Invalid or expired OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Submit PIN
  const submitPIN = async () => {
    if (!pin || pin.length !== 4) {
      setErrorMessage("Please enter your 4-digit PIN");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setStatusMessage("Processing payment... Please wait");

    try {
      const res = await axios.post(PAYSTACK_PROXY_URL, {
        action: "submit_pin",
        reference,
        pin,
      });

      if (!res.data.success) {
        throw new Error(res.data.message || "PIN verification failed");
      }

      const { status } = res.data.data;

      if (status === "success") {
        setStatusMessage("Payment successful! Finalizing license...");
        await activateLicense(reference);
      } else {
        pollPayment(reference);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(
        err?.response?.data?.message ||
        "PIN incorrect or payment failed. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Activate license
  const activateLicense = async (ref) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const res = await axios.post(
        "https://installmentzone.com/pharmacy/api/renew_certificate.php",
        { pack: pack.code, reference: ref },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "success") {
        alert("License activated successfully!");
        navigate("/dashboard");
      } else {
        setErrorMessage(res.data.message || "License activation failed");
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Failed to activate license. Contact support.";
      setErrorMessage(msg);
    }
  };

  // OTP Countdown
  useEffect(() => {
    if (otpCountdown <= 0) return;

    const timer = setInterval(() => {
      setOtpCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (showOTP) setErrorMessage("OTP expired. Please restart payment.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpCountdown, showOTP]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>{pack.name} License Renewal</h2>
        <div style={styles.details}>
          <p>Duration: <strong>{pack.duration}</strong></p>
          <p style={styles.price}>Amount: <strong>{formatGhc(pack.price)}</strong></p>
        </div>

        {(loading || statusMessage) && (
          <div style={styles.statusBox}>
            <p style={{ fontWeight: "bold", color: loading ? "#2563eb" : "#047857" }}>
              {statusMessage}
            </p>
            {otpCountdown > 0 && showOTP && (
              <p style={{ color: "#b45309", marginTop: 8 }}>
                OTP expires in:{" "}
                <strong>
                  {Math.floor(otpCountdown / 60)}:
                  {(otpCountdown % 60).toString().padStart(2, "0")}
                </strong>
              </p>
            )}
          </div>
        )}

        {errorMessage && <div style={styles.errorBox}>{errorMessage}</div>}

        {!showOTP && !showPIN && (
          <>
            <h3 style={{ marginBottom: "16px", color: "#1e40af", fontSize: "1.25rem" }}>
              Select Mobile Money Network
            </h3>

            <div style={styles.providerGrid}>
              {[
                {
                  value: "mtn",
                  name: "MTN MoMo",
                  logo: "https://installmentzone.com/pharmacy/operators/mtn.png",
                },
                {
                  value: "vod",
                  name: "Telecel Cash",
                  logo: "https://installmentzone.com/pharmacy/operators/voda.png",
                },
                {
                  value: "tgo",
                  name: "AirtelTigo Money",
                  logo: "https://installmentzone.com/pharmacy/operators/airtel.png",
                },
              ].map((item) => (
                <div
                  key={item.value}
                  style={{
                    ...styles.providerCard,
                    borderColor: provider === item.value ? "#2563eb" : "#d1d5db",
                    boxShadow: provider === item.value ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                  }}
                  onClick={() => setProvider(item.value)}
                >
                  <img src={item.logo} alt={item.name} style={styles.providerLogo} />
                  <span style={{ fontSize: "0.9rem", marginTop: 6, fontWeight: provider === item.value ? "600" : "normal" }}>
                    {item.name}
                  </span>
                </div>
              ))}
            </div>

            <input
              style={styles.input}
              type="tel"
              placeholder="Enter MoMo Number (e.g. 0241234567)"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              maxLength={10}
              disabled={loading}
            />

            <button
              style={{
                ...styles.button,
                background: loading ? "#9ca3af" : "#2563eb",
              }}
              onClick={startPayment}
              disabled={loading}
            >
              {loading ? "Processing..." : `Pay ${formatGhc(pack.price)}`}
            </button>
          </>
        )}

        {showOTP && (
          <>
            <h3 style={styles.sectionTitle}>Enter OTP</h3>
            <p style={styles.instruction}>
              An OTP has been sent to <strong>{phone}</strong> via SMS or your MoMo app.<br />
              Please enter it below (expires soon).
            </p>

            <input
              style={styles.input}
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              maxLength={6}
              disabled={loading}
            />

            <button
              style={{
                ...styles.button,
                background: loading ? "#9ca3af" : "#059669",
              }}
              onClick={submitOTP}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Submit OTP"}
            </button>
          </>
        )}

        {showPIN && (
          <>
            <h3 style={styles.sectionTitle}>Enter MoMo PIN</h3>
            <p style={styles.instruction}>
              Approve the transaction on your phone by entering your 4-digit MoMo PIN.
            </p>

            <input
              style={styles.input}
              type="password"
              placeholder="Enter 4-digit PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              maxLength={4}
              disabled={loading}
            />

            <button
              style={{
                ...styles.button,
                background: loading ? "#9ca3af" : "#059669",
              }}
              onClick={submitPIN}
              disabled={loading}
            >
              {loading ? "Processing..." : "Submit PIN"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f3f4f6, #e5e7eb)",
    padding: "20px",
  },
  card: {
    background: "#ffffff",
    padding: "40px 30px",
    borderRadius: "16px",
    boxShadow: "0 15px 35px rgba(0,0,0,0.12)",
    width: "100%",
    maxWidth: "420px",
    textAlign: "center",
  },
  title: {
    fontSize: "1.8rem",
    margin: "0 0 20px",
    color: "#1f2937",
  },
  details: {
    marginBottom: "30px",
    fontSize: "1.1rem",
    color: "#4b5563",
  },
  price: {
    fontSize: "1.5rem",
    marginTop: "8px",
    color: "#111827",
  },
  statusBox: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: "8px",
    padding: "16px",
    margin: "20px 0",
    fontSize: "1.05rem",
  },
  errorBox: {
    background: "#fee2e2",
    border: "1px solid #fca5a5",
    borderRadius: "8px",
    padding: "14px",
    margin: "16px 0",
    color: "#b91c1c",
    fontWeight: "500",
  },
  sectionTitle: {
    fontSize: "1.4rem",
    margin: "20px 0 12px",
    color: "#1e40af",
  },
  instruction: {
    fontSize: "0.95rem",
    color: "#4b5563",
    marginBottom: "20px",
    lineHeight: "1.5",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    margin: "12px 0",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "1.05rem",
    background: "#f9fafb",
  },
  button: {
    marginTop: "20px",
    padding: "14px",
    width: "100%",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "1.1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.2s",
  },

  // New styles for visual provider selection
  providerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "12px",
    marginBottom: "24px",
  },
  providerCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "12px 8px",
    border: "2px solid #d1d5db",
    borderRadius: "12px",
    cursor: "pointer",
    background: "#f9fafb",
    transition: "all 0.2s ease",
  },
  providerLogo: {
    width: "64px",
    height: "64px",
    objectFit: "contain",
    marginBottom: "8px",
  },
};

export default PaymentPage;